/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {CommitChange, Diff, ModifyChange, RenameChange} from './github/diffTypes';
import type {GitObjectID} from './github/types';

import DiffContentSkeleton from './DiffContentSkeleton';
import DiffTableOfContents from './DiffTableOfContents';
import LargeDiffPlaceholder from './LargeDiffPlaceholder';
import {FileHeader} from './SplitDiffFileHeader';
import SplitDiffView from './SplitDiffView';
import hasBinaryContent from './hasBinaryContent';
import joinPath from './joinPath';
import {fileContentsDeltaAtom, forceLoadDiffPathAtom, gitHubBlobAtom, gitHubPullRequestFileStatsAtom} from './jotai/atoms';
import {fileViewedStateAtom} from './jotai';
import type {FileStats} from './jotai';
import {getPathForChange} from './utils';
import {Box, Text} from '@primer/react';
import {useAtomValue} from 'jotai';
import React, {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react';

/**
 * Number of off-screen neighbor files (above and below) to pre-activate
 * beyond the files that are actually in the viewport. This ensures the
 * next file is ready before the user scrolls to it.
 */
const NEIGHBOR_PREFETCH_COUNT = 5;
/**
 * Height of the LargeDiffPlaceholder in approximate diff-row equivalents.
 * The placeholder is ~120px (icon + "Load Diff" link + description + line count).
 * At 20px per row, that's ~6 rows.
 */
const PLACEHOLDER_ROW_ESTIMATE = 6;

/**
 * Threshold (in additions+deletions) above which a diff will likely exceed
 * LARGE_DIFF_LINE_THRESHOLD (500 total hunk lines including context) and
 * render a LargeDiffPlaceholder instead of the full diff.
 * Conservative: slightly below 500 to account for context lines added per hunk.
 */
const LARGE_DIFF_STATS_THRESHOLD = 400;

/**
 * Estimate the number of visible diff rows from the file's addition/deletion
 * counts and change type.
 *
 * Accounts for three rendering paths:
 *  1. Deleted files → always show LargeDiffPlaceholder (short)
 *  2. Large diffs (>~400 changed lines) → LargeDiffPlaceholder (short)
 *  3. Normal diffs → full diff height estimate
 */
function estimateDiffLineCount(stats: FileStats | undefined): number | undefined {
  if (stats == null) {
    return undefined;
  }
  const {additions, deletions, changeType} = stats;

  // Deleted files always render as a LargeDiffPlaceholder (never inline diff)
  if (changeType === 'DELETED') {
    return PLACEHOLDER_ROW_ESTIMATE;
  }

  // Large diffs show LargeDiffPlaceholder with "Load Diff" button
  if (additions + deletions > LARGE_DIFF_STATS_THRESHOLD) {
    return PLACEHOLDER_ROW_ESTIMATE;
  }

  // Normal diff: max() because in split view paired adds/removes share rows,
  // plus a buffer for context lines and hunk separators.
  return Math.max(additions, deletions) + Math.min(additions, deletions, 6) + 6;
}

/**
 * Hook that manages a single IntersectionObserver for all file sentinels.
 * Returns a Set of indices that are currently in the viewport, and a
 * ref-callback to register each file's sentinel element by index.
 */
function useFileVisibility(): {
  activatedIndices: Set<number>;
  registerSentinel: (index: number, el: HTMLDivElement | null) => void;
} {
  // Set of indices that have been activated (once activated, never removed).
  const [activatedIndices, setActivatedIndices] = useState<Set<number>>(() => new Set());
  // Map from sentinel element → index, for the observer callback.
  const elementToIndex = useRef<Map<Element, number>>(new Map());
  // Map from index → element, so we can manage observer.observe/unobserve.
  const indexToElement = useRef<Map<number, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Tracks which indices are currently intersecting the viewport.
  const visibleIndices = useRef<Set<number>>(new Set());

  // Create the observer once on mount.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        let changed = false;
        for (const entry of entries) {
          const idx = elementToIndex.current.get(entry.target);
          if (idx == null) continue;
          if (entry.isIntersecting) {
            if (!visibleIndices.current.has(idx)) {
              visibleIndices.current.add(idx);
              changed = true;
            }
          } else {
            if (visibleIndices.current.has(idx)) {
              visibleIndices.current.delete(idx);
              changed = true;
            }
          }
        }
        if (changed) {
          // Compute activated = visible ∪ neighbors
          const next = new Set<number>();
          for (const idx of visibleIndices.current) {
            for (let d = -NEIGHBOR_PREFETCH_COUNT; d <= NEIGHBOR_PREFETCH_COUNT; d++) {
              const neighbor = idx + d;
              if (neighbor >= 0) {
                next.add(neighbor);
              }
            }
          }
          setActivatedIndices(prev => {
            // Merge: once activated, always activated.
            let merged = prev;
            for (const idx of next) {
              if (!merged.has(idx)) {
                if (merged === prev) {
                  merged = new Set(prev);
                }
                merged.add(idx);
              }
            }
            return merged;
          });
        }
      },
      // No rootMargin — we detect actual viewport intersection,
      // and handle prefetching via neighbor indices instead.
    );
    observerRef.current = observer;

    // Observe any elements that were registered before the observer was created.
    for (const [idx, el] of indexToElement.current) {
      elementToIndex.current.set(el, idx);
      observer.observe(el);
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const registerSentinel = useCallback((index: number, el: HTMLDivElement | null) => {
    const prevEl = indexToElement.current.get(index);
    if (prevEl === el) return; // no change

    // Unobserve the old element.
    if (prevEl != null) {
      observerRef.current?.unobserve(prevEl);
      elementToIndex.current.delete(prevEl);
      indexToElement.current.delete(index);
    }

    // Observe the new element.
    if (el != null) {
      indexToElement.current.set(index, el);
      elementToIndex.current.set(el, index);
      observerRef.current?.observe(el);
    }
  }, []);

  return {activatedIndices, registerSentinel};
}

function LazyDiffFile({
  change,
  isPullRequest,
  path,
  fileStats,
  isActivated: parentActivated,
  registerSentinel,
  index,
}: {
  change: CommitChange;
  isPullRequest: boolean;
  path: string;
  fileStats: FileStats | undefined;
  isActivated: boolean;
  registerSentinel: (index: number, el: HTMLDivElement | null) => void;
  index: number;
}): React.ReactElement {
  const [forceActivated, setForceActivated] = useState(false);
  const isActivated = parentActivated || forceActivated;
  const sentinelRef = useCallback(
    (el: HTMLDivElement | null) => registerSentinel(index, el),
    [registerSentinel, index],
  );
  const forceLoadDiffPath = useAtomValue(forceLoadDiffPathAtom);
  const viewedState = useAtomValue(fileViewedStateAtom(path));
  const [open, setOpen] = useState(viewedState !== 'VIEWED');

  // Sync collapsed state when the PR data loads and reveals the actual viewed
  // state. Without this, files start open (because viewedState defaults to
  // UNVIEWED before PR data arrives) and only collapse after the data loads.
  const prevViewedRef = useRef(viewedState);
  useEffect(() => {
    if (prevViewedRef.current !== viewedState) {
      prevViewedRef.current = viewedState;
      setOpen(viewedState !== 'VIEWED');
    }
  }, [viewedState]);

  // Force-activate when this file is targeted by a comment jump or sidebar
  // click, even if it's far outside the viewport.
  useEffect(() => {
    if (!isActivated && forceLoadDiffPath === path) {
      setForceActivated(true);
    }
  }, [isActivated, forceLoadDiffPath, path]);

  const estimatedLines = estimateDiffLineCount(fileStats);

  if (!isActivated) {
    return (
      <div ref={sentinelRef}>
        <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2}>
          <FileHeader path={path} open={open} onChangeOpen={setOpen} isPullRequest={isPullRequest} />
          {open && <DiffContentSkeleton lineCount={estimatedLines} />}
        </Box>
      </div>
    );
  }

  return (
    <div ref={sentinelRef}>
      <Suspense fallback={
        <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2}>
          <FileHeader path={path} open={open} onChangeOpen={setOpen} isPullRequest={isPullRequest} />
          {open && <DiffContentSkeleton lineCount={estimatedLines} />}
        </Box>
      }>
        <ChangeDisplay change={change} isPullRequest={isPullRequest} />
      </Suspense>
    </div>
  );
}

export default function DiffView({diff, isPullRequest}: {diff: Diff; isPullRequest: boolean}) {
  const fileStatsMap = useAtomValue(gitHubPullRequestFileStatsAtom);

  // Sort files to match the tree view: directories before files at each level.
  // e.g. ".codistry/x" and "src/y" (in directories) come before "package.json" (root file).
  const sortedDiff = useMemo(
    () =>
      [...diff].sort((a, b) => {
        const pathA = getPathForChange(a);
        const pathB = getPathForChange(b);
        const partsA = pathA.split('/');
        const partsB = pathB.split('/');
        const minLen = Math.min(partsA.length, partsB.length);
        for (let i = 0; i < minLen; i++) {
          if (partsA[i] !== partsB[i]) {
            const aIsDir = i < partsA.length - 1;
            const bIsDir = i < partsB.length - 1;
            // Directories come before files at the same level
            if (aIsDir && !bIsDir) return -1;
            if (!aIsDir && bIsDir) return 1;
            return partsA[i].localeCompare(partsB[i]);
          }
        }
        return partsA.length - partsB.length;
      }),
    [diff],
  );

  const {activatedIndices, registerSentinel} = useFileVisibility();

  if (diff != null) {
    return (
      <div>
        <Box paddingY={2}>
          <DiffTableOfContents diff={sortedDiff} />
        </Box>
        {sortedDiff.map((change, index) => {
          const name = change.type === 'modify'
            ? change.before.name
            : change.type === 'rename'
              ? change.after.name
              : change.entry.name;
          const basePath = change.type === 'rename' ? change.newBasePath : change.basePath;
          const path = joinPath(basePath, name);
          return (
            <Box paddingY={1} data-toc-path={path} key={path}>
              <LazyDiffFile
                change={change}
                isPullRequest={isPullRequest}
                path={path}
                fileStats={fileStatsMap.get(path)}
                isActivated={activatedIndices.has(index)}
                registerSentinel={registerSentinel}
                index={index}
              />
            </Box>
          );
        })}
      </div>
    );
  } else {
    return <div>commit not found or fetched from GitHub URL above</div>;
  }
}

function ChangeDisplay({change, isPullRequest}: {change: CommitChange; isPullRequest: boolean}) {
  switch (change.type) {
    case 'add': {
      const {basePath, entry} = change;
      const {name, oid} = entry;
      return <AddedFile basePath={basePath} name={name} oid={oid} isPullRequest={isPullRequest} />;
    }
    case 'remove': {
      const {basePath, entry} = change;
      const {name, oid} = entry;
      return <RemovedFile basePath={basePath} name={name} oid={oid} isPullRequest={isPullRequest} />;
    }
    case 'modify': {
      return <ModifiedFile modify={change} isPullRequest={isPullRequest} />;
    }
    case 'rename': {
      return <RenamedFile rename={change} isPullRequest={isPullRequest} />;
    }
  }
}

function AddedFile({
  basePath,
  name,
  oid,
  isPullRequest,
}: {
  basePath: string;
  name: string;
  oid: GitObjectID;
  isPullRequest: boolean;
}) {
  const path = joinPath(basePath, name);
  const blobAtom = useMemo(() => gitHubBlobAtom(oid), [oid]);
  const blob = useAtomValue(blobAtom);
  const {isBinary, text} = blob ?? {};
  // Check both the isBinary flag and perform our own binary content detection
  if (text != null && !isBinary && !hasBinaryContent(text)) {
    return (
      <SplitDiffView path={path} before={null} after={oid} isPullRequest={isPullRequest} />
    );
  } else {
    return <BinaryFile path={path} />;
  }
}

function RemovedFile({
  basePath,
  name,
  oid,
  isPullRequest,
}: {
  basePath: string;
  name: string;
  oid: GitObjectID;
  isPullRequest: boolean;
}) {
  const path = joinPath(basePath, name);
  const blobAtom = useMemo(() => gitHubBlobAtom(oid), [oid]);
  const blob = useAtomValue(blobAtom);
  const {isBinary, text} = blob ?? {};
  const [diffLoaded, setDiffLoaded] = useState(false);
  const viewedState = useAtomValue(fileViewedStateAtom(path));
  const [open, setOpen] = useState(viewedState !== 'VIEWED');

  const handleLoadDiff = useCallback(() => {
    setDiffLoaded(true);
  }, []);

  if (text == null || isBinary || hasBinaryContent(text)) {
    return <BinaryFile path={path} />;
  }

  const lineCount = text.split('\n').length;
  const canLoadDiff = lineCount <= 10000;

  if (diffLoaded && canLoadDiff) {
    return (
      <SplitDiffView path={path} before={oid} after={null} isPullRequest={isPullRequest} />
    );
  }

  return (
    <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2}>
      <FileHeader path={path} linesRemoved={lineCount} open={open} onChangeOpen={setOpen} isPullRequest={isPullRequest} />
      {open && (
        <LargeDiffPlaceholder
          onLoadDiff={canLoadDiff ? handleLoadDiff : undefined}
          totalLines={lineCount}
          description="This file was deleted."
          variant="deleted"
        />
      )}
    </Box>
  );
}

function ModifiedFile({modify, isPullRequest}: {modify: ModifyChange; isPullRequest: boolean}) {
  const {basePath, before, after} = modify;
  const path = joinPath(basePath, before.name);
  const fileMod = useMemo(
    () => ({
      before: before.oid,
      after: after.oid,
      path,
    }),
    [before.oid, after.oid, path],
  );
  const fileModAtom = useMemo(() => fileContentsDeltaAtom(fileMod), [fileMod]);
  const delta = useAtomValue(fileModAtom);
  const {before: beforeBlob, after: afterBlob} = delta;
  if (beforeBlob == null || afterBlob == null) {
    // Something went wrong?
    return null;
  }

  // Check both the isBinary flag and perform our own binary content detection
  if (
    beforeBlob.isBinary ||
    afterBlob.isBinary ||
    hasBinaryContent(beforeBlob.text) ||
    hasBinaryContent(afterBlob.text)
  ) {
    // We could handle this more gracefully, particularly if only one of the
    // two files is binary, but this is good enough, for now.
    return <BinaryFile path={path} />;
  } else if (beforeBlob.text == null || afterBlob.text == null) {
    // Something went wrong?
    return null;
  } else {
    return (
      <SplitDiffView
        path={path}
        before={beforeBlob.oid}
        after={afterBlob.oid}
        isPullRequest={isPullRequest}
      />
    );
  }
}

function RenamedFile({rename, isPullRequest}: {rename: RenameChange; isPullRequest: boolean}) {
  const {oldBasePath, newBasePath, before, after} = rename;
  const newPath = joinPath(newBasePath, after.name);
  const oldPath = joinPath(oldBasePath, before.name);
  const viewedState = useAtomValue(fileViewedStateAtom(newPath));
  const [open, setOpen] = useState(viewedState !== 'VIEWED');

  if (before.oid === after.oid) {
    // Exact rename — no content changes. Collapsible with viewed checkbox.
    return (
      <Box borderWidth="1px" borderStyle="solid" borderColor="border.default" borderRadius={2}>
        <FileHeader path={newPath} oldPath={oldPath} open={open} onChangeOpen={setOpen} isPullRequest={isPullRequest} />
        {open && (
          <Box px={3} py={2} sx={{bg: 'canvas.subtle', fontSize: '13px', color: 'fg.muted'}}>
            File renamed without changes
          </Box>
        )}
      </Box>
    );
  }

  // Rename + content changes — render as a modify diff.
  const fileMod = useMemo(
    () => ({
      before: before.oid,
      after: after.oid,
      path: newPath,
    }),
    [before.oid, after.oid, newPath],
  );
  const fileModAtom = useMemo(() => fileContentsDeltaAtom(fileMod), [fileMod]);
  const delta = useAtomValue(fileModAtom);
  const {before: beforeBlob, after: afterBlob} = delta;
  if (beforeBlob == null || afterBlob == null) {
    return null;
  }

  if (
    beforeBlob.isBinary ||
    afterBlob.isBinary ||
    hasBinaryContent(beforeBlob.text) ||
    hasBinaryContent(afterBlob.text)
  ) {
    return <BinaryFile path={newPath} />;
  } else if (beforeBlob.text == null || afterBlob.text == null) {
    return null;
  } else {
    return (
      <SplitDiffView
        path={newPath}
        oldPath={oldPath}
        before={beforeBlob.oid}
        after={afterBlob.oid}
        isPullRequest={isPullRequest}
      />
    );
  }
}

function BinaryFile({path}: {path: string}) {
  return (
    <Box>
      <FileHeader path={path} />
      <Text>Binary file not shown.</Text>
    </Box>
  );
}
